import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Flame, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  StreakBadge,
  getEarnedBadges,
  getNextBadgeProgress,
  streakBadges,
} from "@/lib/streakHelpers";

interface StreakBadgesProps {
  currentStreak: number;
  longestStreak: number;
  className?: string;
}

export const StreakBadges = ({
  currentStreak,
  longestStreak,
  className,
}: StreakBadgesProps) => {
  const earnedBadges = getEarnedBadges(currentStreak);
  const { badge: nextBadge, progress, daysRemaining } = getNextBadgeProgress(currentStreak);

  return (
    <Card className={className}>
      <CardContent className="pt-6 space-y-6">
        {/* Current Streak Display */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Flame className="h-6 w-6 text-orange-500" />
            <span className="text-3xl font-bold">{currentStreak}</span>
            <span className="text-muted-foreground">day streak</span>
          </div>
          {longestStreak > currentStreak && (
            <p className="text-xs text-muted-foreground">
              Longest streak: {longestStreak} days
            </p>
          )}
        </div>

        {/* Next Badge Progress */}
        {nextBadge && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Next Badge</span>
              <span className="text-sm text-muted-foreground">
                {daysRemaining} days to go
              </span>
            </div>
            <Progress value={progress} className="h-2 mb-2" />
            <div className="flex items-center gap-2">
              <span className="text-2xl">{nextBadge.icon}</span>
              <div className="flex-1">
                <p className="text-sm font-semibold">{nextBadge.title}</p>
                <p className="text-xs text-muted-foreground">
                  Complete {nextBadge.days} days in a row
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Badge Collection */}
        <div>
          <h3 className="text-sm font-medium mb-3">Your Badges</h3>
          <div className="grid grid-cols-3 gap-3">
            {streakBadges.map((badge) => {
              const isEarned = earnedBadges.some((b) => b.days === badge.days);
              return (
                <div
                  key={badge.days}
                  className={cn(
                    "relative rounded-lg border p-3 text-center transition-all",
                    isEarned
                      ? `${badge.bgColor} ${badge.borderColor}`
                      : "border-border/50 bg-muted/20 opacity-50"
                  )}
                >
                  {!isEarned && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-lg">
                      <Lock className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="text-3xl mb-1">{badge.icon}</div>
                  <p className="text-xs font-semibold leading-tight">
                    {badge.title}
                  </p>
                  <Badge
                    variant="outline"
                    className={cn(
                      "mt-1 text-xs",
                      isEarned && badge.color
                    )}
                  >
                    {badge.days} days
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
